import { useCallback } from "react";
import { Bundle, Resource } from "@medplum/fhirtypes";
import { MappedConsolidatedResources } from "./consolidated-context/reducer";
import { bundleGroupingWorkerScript } from "./workers/bundleGroupingWorker";

export type WorkerResponse = {
  type: "GROUP_BUNDLE";
  requestId: string;
  mappedResources?: MappedConsolidatedResources;
  processingTime?: number;
  error?: string;
};

/**
 * Process a FHIR bundle using web worker to group resources by type
 */
export async function processBundleWithWorker(
  bundle: Bundle<Resource>
): Promise<MappedConsolidatedResources> {
  return new Promise((resolve, reject) => {
    try {
      /**
       * Create a web worker from the bundleGroupingWorkerScript string.
       * This approach uses Blob + URL.createObjectURL instead of loading from a file path,
       * which avoids module system issues and file path dependencies. The worker script
       * is embedded as a string and executed in the worker context at runtime.
       */
      const blob = new Blob([bundleGroupingWorkerScript], { type: "application/javascript" });
      const workerScript = URL.createObjectURL(blob);
      const worker = new Worker(workerScript);

      const timeout = setTimeout(() => {
        worker.terminate();
        reject(new Error("Worker timeout"));
      }, 30000);

      worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        clearTimeout(timeout);
        worker.terminate();

        if (event.data.error) {
          reject(new Error(event.data.error));
        } else if (event.data.mappedResources) {
          resolve(event.data.mappedResources);
        } else {
          reject(new Error("No mapped resources returned"));
        }
      };

      worker.onerror = error => {
        clearTimeout(timeout);
        worker.terminate();
        reject(error);
      };

      worker.postMessage(bundle);
    } catch (error) {
      console.error("Web worker failed:", error);
      reject(error);
    }
  });
}

export function useBundleGroupingWorker() {
  const processBundle = useCallback((bundle: Bundle<Resource>) => {
    return processBundleWithWorker(bundle);
  }, []);

  return processBundle;
}
